import React, { ForwardedRef, forwardRef, ReactElement, useCallback, useRef } from 'react';
import { ColorScheme } from '@vis.gl/react-google-maps';

import { defaults } from '../../consts';
import { lightingEffect } from '../../effects';
import { BaseMapConfig, DeckGLRef, ReactDeckProps } from '../../types';
import { DeckGLOverlay } from './DeckGLOverlay';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import DeckGL from '@deck.gl/react';

/**
 * Base deck-gl or Google Map React component for all maps.
 * Includes a configurable static map and accepts deck.gl map layers
 * as an array of objects passed to the layers prop.
 */
export const BaseMap = forwardRef<DeckGLRef, ReactDeckProps & BaseMapConfig>(function BaseMapForwardRef(
    props: BaseMapConfig,
    ref: ForwardedRef<DeckGLRef>
): ReactElement {
    // Setup for getting the Deck.gl object through reference
    const deck = useRef<DeckGLRef | null>(null);
    const deckRefFn = useCallback((r: DeckGLRef | null) => {
        if (r) {
            deck.current = r;
            if (ref) {
                typeof ref === 'function' ? ref(r) : (ref.current = r);
            }
        }
    }, []);

    // Tooltip picking logic
    const { layers = [], pickingRadiusPixels = 5 } = props;

    const includes3dHex: boolean = layers.some(
        (layer) => layer && 'props' in layer && 'extruded' in layer.props && layer.props.extruded,
        false
    );

    const {
        gestureHandling,
        getTooltip,
        googleMapsAPIKey,
        initialViewState,
        mapId,
        mapType,
        onClick,
        onHover,
        onViewStateChange,
        onInteractionStateChange,
        viewState,
        restriction,
        maxZoom,
        minZoom,
        colorScheme
    } = props;
    const lat = viewState?.latitude || initialViewState?.latitude || defaults.basemap.initialViewState.latitude;

    const lng = viewState?.longitude || initialViewState?.longitude || defaults.basemap.initialViewState.longitude;

    const googleViewState = viewState || initialViewState || defaults.basemap.initialViewState;

    return (
        <>
            {/* If a Google Maps API key is missing, this renders the DeckGL layers without a base map under them */}
            {!googleMapsAPIKey ? (
                <DeckGL
                    ref={deckRefFn}
                    controller
                    layers={layers}
                    viewState={viewState}
                    initialViewState={initialViewState}
                    effects={includes3dHex ? [lightingEffect] : []}
                    pickingRadius={pickingRadiusPixels}
                    getTooltip={getTooltip}
                    onViewStateChange={onViewStateChange}
                    onInteractionStateChange={onInteractionStateChange}
                    style={{ width: '100%', height: '100%' }}
                />
            ) : (
                <APIProvider apiKey={googleMapsAPIKey}>
                    <Map
                        key={colorScheme}
                        /** Set the default latitude and longitude */
                        defaultCenter={{ lat, lng }}
                        /** Whether to allow clicking on Google points of interest, which opens a popup. */
                        clickableIcons={false}
                        /** Remove UI buttons on the Google Map in favor for our UI */
                        disableDefaultUI={true}
                        /** Prevents users from accidentally rotating map */
                        headingInteractionEnabled={false}
                        /** Allow the user to zoom by fractions instead of whole numbers */
                        isFractionalZoomEnabled={true}
                        /**
                         * When a Google map is part of a page, gestureHandling specifies how
                         * a user can zoom the map.
                         * https://developers.google.com/maps/documentation/javascript/interaction
                         **/
                        gestureHandling={gestureHandling ?? 'greedy'}
                        /** Optional map id used for custom maps. */
                        mapId={mapId}
                        /**
                         * The default base map style. Either hybrid (satellite) view or base roadmap view
                         * https://developers.google.com/maps/documentation/javascript/maptypes#BasicMapTypes
                         */
                        mapTypeId={mapType}
                        /** Show the distance scale */
                        scaleControl={true}
                        /** Set the map's color scheme */
                        colorScheme={colorScheme === 'light' ? ColorScheme.LIGHT : ColorScheme.DARK}
                        /**
                         * Map style customizations for decluttering and fine-tuning (only applied when there is no custom
                         * mapId).
                         *
                         * This styling is needed to fine-tune the satellite basemap specifically, as Cloud Styling
                         * (currently in Preview) isn't yet available for these tiles.
                         * See https://developers.google.com/maps/documentation/javascript/style-reference
                         */
                        {...(!mapId && {
                            styles: [
                                {
                                    featureType: 'administrative',
                                    elementType: 'labels',
                                    stylers: [{ weight: '2' }]
                                },
                                {
                                    featureType: 'administrative.neighborhood',
                                    stylers: [{ visibility: 'off' }]
                                },
                                {
                                    featureType: 'landscape.man_made',
                                    stylers: [{ visibility: 'off' }]
                                },
                                {
                                    featureType: 'poi',
                                    stylers: [{ visibility: 'off' }]
                                },
                                {
                                    featureType: 'road',
                                    stylers: [{ visibility: 'off' }]
                                },
                                {
                                    featureType: 'transit',
                                    stylers: [{ visibility: 'off' }]
                                }
                            ]
                        })}
                        /** Set the tilt of the map in 3D view */
                        tilt={viewState?.pitch}
                        /** Prevents users from tilting map */
                        tiltInteractionEnabled={false}
                        /** Set the default zoom */
                        defaultZoom={viewState?.zoom}
                        /** Limit pan */
                        restriction={restriction}
                        maxZoom={maxZoom}
                        minZoom={minZoom}
                        style={{ width: '100%', height: '100%' }}
                    >
                        <DeckGLOverlay
                            data-testid="google-base-map"
                            gestureHandling={gestureHandling}
                            getTooltip={getTooltip}
                            effects={includes3dHex ? [lightingEffect] : undefined}
                            pickingRadius={pickingRadiusPixels}
                            onClick={onClick}
                            onHover={onHover}
                            layers={layers}
                            viewState={googleViewState}
                            onViewStateChange={onViewStateChange}
                            onInteractionStateChange={onInteractionStateChange}
                            deckRef={deckRefFn}
                        />
                    </Map>
                </APIProvider>
            )}
        </>
    );
});

export default BaseMap;
